import { ITimeOccurrenceDatatype } from '../../datastore/time/data_types';
import { IRawTimeOccurrenceDatatype } from '../../datastore/time/data_types';
import { TimeStartEndIdentifierEnum, TimeStartEndDefinitionEnum, EmptyTimeFinderEnum } from './time_types';
import { TimeOccurrenceServant } from '../../servants/time/time_occurrence_servant';
import { PhxCalendarConfiguration } from '../../config/calendar_configuration';

export class TimeOccurrenceEngine {
  constructor(
    private TimeOccurrenceBuilder: any,
    private ngTimeOccurrenceServant: TimeOccurrenceServant,
    private ngPhxCalendarConfiguration: PhxCalendarConfiguration
  ) {}

  // snapClosestTimeBoundary(timeBlock: ITimeOccurrenceDatatype, boundaryType: string): ITimeOccurrenceDatatype {
  //   // May not have to use this method
  //   let boundary = parseInt(boundaryType) || 15;
  //   let toMinutes = timeBlock.to.minutes();
  //   let fromMinutes = timeBlock.from.minutes();
  //   timeBlock.to.minutes(Math.round(toMinutes/boundary)*boundary);
  //   timeBlock.from.minutes(Math.round(fromMinutes/boundary)*boundary);
  //   let rawTimeOccurrence = this.Interface.serialize(this.TimeOccurrenceDTD, timeBlock);
  //   // FIXME engine should not call datasource
  //   return this.ngTimeOccurrenceDataSource.buildTime(rawTimeOccurrence);
  // }

  areTimesFullyOverlapping(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): boolean {
    return (
      timeBlock1.day.isEqual(timeBlock2.day) &&
      timeBlock1.from.isSame(timeBlock2.from) &&
      timeBlock1.to.isSame(timeBlock2.to)
    );
  }

  isTimeWithinTime(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): boolean {
    return (
      timeBlock1.day.isEqual(timeBlock2.day) &&
      timeBlock1.from.isSameOrAfter(timeBlock2.from) &&
      timeBlock1.to.isSameOrBefore(timeBlock2.to)
    );
  }

  diffTimes(
    timeBlock1: ITimeOccurrenceDatatype,
    timeBlock2: ITimeOccurrenceDatatype
  ): ITimeOccurrenceDatatype | undefined {
    return undefined;
  }

  getTimesDistance(
    timeBlock1: ITimeOccurrenceDatatype,
    timeBlock2: ITimeOccurrenceDatatype
  ): ITimeOccurrenceDatatype | undefined {
    return undefined;
  }

  areTimesPartiallyOverlapping(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): boolean {
    return (
      timeBlock1.day === timeBlock2.day &&
      timeBlock1.from.isBefore(timeBlock2.to) &&
      timeBlock1.to.isAfter(timeBlock2.from)
    );
  }

  findEarlierTime(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): ITimeOccurrenceDatatype {
    return timeBlock1.from.isSameOrBefore(timeBlock2.from) ? timeBlock1 : timeBlock2;
  }

  findLaterTime(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): ITimeOccurrenceDatatype {
    return timeBlock1.to.isSameOrAfter(timeBlock2.to) ? timeBlock1 : timeBlock2;
  }

  isTimeEarlier(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): boolean {
    // is timeBlock1 earlier than timeBlock2
    return this.findEarlierTime(timeBlock1, timeBlock2) === timeBlock1;
  }

  isTimeLater(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): boolean {
    // is timeBlock1 later than timeBlock2
    return this.findLaterTime(timeBlock1, timeBlock2) === timeBlock1;
  }

  getTimeStartOrEnd(
    timeBlock1: ITimeOccurrenceDatatype,
    timeBlock2: ITimeOccurrenceDatatype
  ): TimeStartEndIdentifierEnum | undefined {
    // see if timeBlock1 is at the start or end of timeBlock2; the threshold is in MinimumTimeBoundaryDifference
    if (Math.abs(timeBlock1.from.diff(timeBlock2.from)) < TimeStartEndDefinitionEnum.MinimumTimeBoundaryDifference) {
      return TimeStartEndIdentifierEnum.Start;
    } else if (Math.abs(timeBlock1.to.diff(timeBlock2.to)) < TimeStartEndDefinitionEnum.MinimumTimeBoundaryDifference) {
      return TimeStartEndIdentifierEnum.End;
    } else {
      return undefined;
    }
  }

  createMergedTime(timeBlock1: ITimeOccurrenceDatatype, timeBlock2: ITimeOccurrenceDatatype): ITimeOccurrenceDatatype {
    const earlierTime: ITimeOccurrenceDatatype = this.findEarlierTime(timeBlock1, timeBlock2);
    const laterTime: ITimeOccurrenceDatatype = this.findLaterTime(timeBlock1, timeBlock2);

    let rawMergedTimeObj: IRawTimeOccurrenceDatatype = {
      day_of_week: timeBlock1.day.ordinal,
      from_tm: earlierTime.from.format('HH:mm:ss'),
      to_tm: laterTime.to.format('HH:mm:ss')
    };

    // mergedTime = this.ngTimeOccurrenceDataSource.buildTime(rawMergedTimeObj);
    return new this.TimeOccurrenceBuilder(rawMergedTimeObj);
  }

  // Given a range of days (DayOfWeekEnum ordinal values) and a range of times, generate all possible one-hour
  // TimeOccurrence blocks.
  createAllPossibleHourTimeOccurrencesInRange(dayRange: number[], timeRange: number[]): ITimeOccurrenceDatatype[] {
    let timeOccurrences: ITimeOccurrenceDatatype[] = [];

    _.forEach(dayRange, (dayValue: number) => {
      _.forEach(timeRange, (timeValue: number) => {
        let rawTimeOccurrenceObj: IRawTimeOccurrenceDatatype = {
          day_of_week: dayValue,
          from_tm: (timeValue as any) + ':00:00',
          to_tm: (timeValue as any) + EmptyTimeFinderEnum.DurationHours + ':00:00'
        };

        // timeOccurrences.push(this.ngTimeOccurrenceDataSource.buildTime(rawTimeOccurrenceObj));
        timeOccurrences.push(new this.TimeOccurrenceBuilder(rawTimeOccurrenceObj));
      });
    });

    return timeOccurrences;
  }

  // Given an array of TimeOccurrence DTO's, find the first hour that doesn't overlap with any in the given array.
  findEmptyHourInTimes(allExistingTimeOccurrences: ITimeOccurrenceDatatype[]): ITimeOccurrenceDatatype | undefined {
    const hourBegin: number = parseInt(this.ngPhxCalendarConfiguration.hourBegin, 10);
    const hourEnd: number = parseInt(this.ngPhxCalendarConfiguration.hourEnd, 10);

    let allPotentialTimeOccurrence: ITimeOccurrenceDatatype[] = this.createAllPossibleHourTimeOccurrencesInRange(
      _.range(EmptyTimeFinderEnum.StartDay, EmptyTimeFinderEnum.EndDay),
      _.range(hourBegin, hourEnd)
    );

    let emptyHour: ITimeOccurrenceDatatype | undefined = _.find(
      allPotentialTimeOccurrence,
      (potentialTime: ITimeOccurrenceDatatype): boolean =>
        !this.doesTimeOverlapWithTimes(potentialTime, allExistingTimeOccurrences)
    );

    return !!emptyHour ? emptyHour : undefined;
  }

  createNewHourTimeOccurrence(
    allExistingTimeOccurrences: ITimeOccurrenceDatatype[],
    potentialTime: IRawTimeOccurrenceDatatype
  ): ITimeOccurrenceDatatype | undefined {
    let timeOccurrences: ITimeOccurrenceDatatype[] = [];
    let rawTimeOccurrenceObj: IRawTimeOccurrenceDatatype = {
      day_of_week: potentialTime.day_of_week,
      from_tm: potentialTime.from_tm + ':00',
      to_tm: potentialTime.to_tm + ':00'
    };

    timeOccurrences.push(new this.TimeOccurrenceBuilder(rawTimeOccurrenceObj));

    let newTimeOccurrence: ITimeOccurrenceDatatype = _.find(
      timeOccurrences,
      (_potentialTime: ITimeOccurrenceDatatype): boolean =>
        !this.doesTimeOverlapWithTimes(_potentialTime, allExistingTimeOccurrences)
    );

    return !!newTimeOccurrence ? newTimeOccurrence : undefined;
  }

  createDefaultTimeOccurrence(): ITimeOccurrenceDatatype {
    let timeOccurrences: ITimeOccurrenceDatatype[] = [];

    let rawTimeOccurrenceObj: IRawTimeOccurrenceDatatype = {
      day_of_week: EmptyTimeFinderEnum.StartDay,
      from_tm: '00:00:00',
      to_tm: '00:00:00'
    };
    timeOccurrences.push(new this.TimeOccurrenceBuilder(rawTimeOccurrenceObj));

    let newTimeOccurrence: ITimeOccurrenceDatatype = new this.TimeOccurrenceBuilder(rawTimeOccurrenceObj);

    return newTimeOccurrence;
  }

  // Given a TimeOccurrence and an array of TimeOccurrences, see if the target block overlaps with any in the array.
  doesTimeOverlapWithTimes(targetTb: ITimeOccurrenceDatatype, allTimes: ITimeOccurrenceDatatype[]): boolean {
    let result: boolean[] = _.map(allTimes, (tb: ITimeOccurrenceDatatype): boolean => {
      return this.areTimesPartiallyOverlapping(targetTb, tb) && targetTb.trackingId !== tb.trackingId;
    });

    return _.some(result);
  }

  findOverlappingTimes(
    targetTb: ITimeOccurrenceDatatype,
    allTimes: ITimeOccurrenceDatatype[]
  ): ITimeOccurrenceDatatype[] {
    return _.filter(allTimes, (timeOccurrence: ITimeOccurrenceDatatype) => {
      return this.areTimesPartiallyOverlapping(targetTb, timeOccurrence);
    });
  }

  createSplitTimes(
    timeToSplit: ITimeOccurrenceDatatype,
    overlappingTimes: ITimeOccurrenceDatatype[]
  ): ITimeOccurrenceDatatype[] {
    let splitTimes: ITimeOccurrenceDatatype[] = [];

    if (_.isEmpty(_.map(overlappingTimes, _.curry(this.areTimesPartiallyOverlapping)(timeToSplit)))) {
      return splitTimes.concat(timeToSplit);
    }

    overlappingTimes = _.sortBy(overlappingTimes, 'fromInput');

    let workTime: ITimeOccurrenceDatatype = _.cloneDeep(timeToSplit);
    workTime.from = timeToSplit.from.clone();
    let firstTime = _.first(overlappingTimes);

    let lastTime = _.last(overlappingTimes);

    if (this.isTimeEarlier(firstTime, timeToSplit)) {
      overlappingTimes = _.tail(overlappingTimes);
      workTime.from = firstTime.to;
    }

    _.each(overlappingTimes, (overlappingTime: ITimeOccurrenceDatatype) => {
      workTime.to = overlappingTime.from;
      splitTimes.push(workTime);
      workTime = _.cloneDeep(timeToSplit);
      workTime.from = overlappingTime.to;
    });

    if (this.isTimeLater(timeToSplit, lastTime)) {
      workTime.to = timeToSplit.to;
      splitTimes.push(workTime);
    }

    const noAdditionalProperties = {};
    return _.map(splitTimes, (time: ITimeOccurrenceDatatype) => {
      return this.ngTimeOccurrenceServant.copyTimeOccurrenceWithProperties(time, noAdditionalProperties);
    });
  }
}

angular
  .module('ui-ng-calendar')
  .factory(
    'ngTimeOccurrenceEngine',
    function (
      TimeOccurrenceBuilder: any,
      ngTimeOccurrenceServant: TimeOccurrenceServant,
      ngPhxCalendarConfiguration: PhxCalendarConfiguration
    ): any {
      return new TimeOccurrenceEngine(TimeOccurrenceBuilder, ngTimeOccurrenceServant, ngPhxCalendarConfiguration);
    }
  );



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-calendar/src/engines/time/time_engine.ts